import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

// Reusable Rules Table Component
export const RulesTable = ({
  rules = [],
  loading = false,
  filterLoading = false,
  error = null,
  stats = { total: 0, active: 0, inactive: 0 },
  searchTerm = '',
  onSearchChange = () => {},
  showFilters = false,
  onToggleFilters = () => {},
  filters = {},
  onFilterChange = () => {},
  onClearFilters = () => {},
  onRetry = () => {},
  onCreateClick = () => {},
  onEditClick = () => {},
  onViewClick = () => {},
  onDeleteClick = () => {},
  getProjectName = () => '',
  getStageColor = () => 'default',
  getStatusColor = () => 'default',
  formatDate = () => '',
  stageOptions = ['event_store'],
  projects = [],
  showCreateButton = true,
  showStats = true,
  showSearchAndFilters = true,
  title = 'Rule Engine Definitions',
  maxWidth = '1200px'
}) => {
  // Filter rules based on search term
  const filteredRules = rules.filter(rule => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      rule.name?.toLowerCase().includes(searchLower) ||
      rule.unique_id?.toLowerCase().includes(searchLower) ||
      rule.stage?.toLowerCase().includes(searchLower) ||
      getProjectName(rule.project_id)?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="content-section">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-section">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, px: 2 }}>
          <Typography variant="h6" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Button variant="outlined" onClick={onRetry}>
            Retry
          </Button>
        </Box>
      </div>
    );
  }

  return (
    <Box sx={{ 
      width: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      height: '100%',
      gap: 2
    }}>
      {/* Rules Statistics Card */}
      {showStats && (
        <Card sx={{ width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h6" gutterBottom>
              ⚖️ Rule Engine Overview
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
              <Box textAlign="center">
                <Typography variant="h4" color="primary">
                  {stats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Rules
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h4" color="success.main">
                  {stats.active}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Rules
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h4" color="error.main">
                  {stats.inactive}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Inactive Rules
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Filters and Search */}
      {showSearchAndFilters && (
        <Card sx={{ width: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                p: 0.5, 
                borderRadius: 1, 
                bgcolor: 'primary.light',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center'
              }}>
                <SearchIcon fontSize="small" />
              </Box>
              <Typography variant="h6" fontWeight="600">
                Search & Filters
              </Typography>
            </Box>
            <Button
              startIcon={<FilterIcon />}
              onClick={onToggleFilters}
              variant={showFilters ? "contained" : "outlined"}
              size="small"
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 2,
                py: 1
              }}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </Box>

          {/* Search Bar */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search rules by name, project name, stage, or rule ID..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              sx: { borderRadius: 2 }
            }}
            sx={{ 
              mb: 3,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              }
            }}
          />

          {/* Filters */}
          {showFilters && (
            <Box sx={{ 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 3,
              bgcolor: 'grey.50'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                  Advanced Filters
                </Typography>
                <Chip 
                  label={`${Object.values(filters).filter(v => v !== '' && v !== null).length} active`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: '600px' }}>
                {projects.length > 0 && (
                  <FormControl size="medium" sx={{ width: '100%' }}>
                    <InputLabel>Project</InputLabel>
                    <Select
                      value={filters.project_id || ''}
                      onChange={(e) => onFilterChange('project_id', e.target.value)}
                      label="Project"
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                All Projects
                              </Typography>
                            </Box>
                          );
                        }
                        const project = projects.find(p => p.unique_id === selected);
                        return project ? project.organization_name : selected;
                      }}
                      startAdornment={
                        <Box sx={{ mr: 1, color: 'text.secondary' }}>
                          🏢
                        </Box>
                      }
                      sx={{ borderRadius: 1 }}
                    >
                      <MenuItem value="">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            All Projects
                          </Typography>
                        </Box>
                      </MenuItem>
                      {projects.map(project => (
                        <MenuItem key={project.unique_id} value={project.unique_id}>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {project.organization_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                              {project.unique_id}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                
                <FormControl size="medium" sx={{ width: '100%' }}>
                  <InputLabel>Stage</InputLabel>
                  <Select
                    value={filters.stage || ''}
                    onChange={(e) => onFilterChange('stage', e.target.value)}
                    label="Stage"
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              All Stages
                            </Typography>
                          </Box>
                        );
                      }
                      return selected.replace('_', ' ').toUpperCase();
                    }}
                    startAdornment={
                      <Box sx={{ mr: 1, color: 'text.secondary' }}>
                        ⚙️
                      </Box>
                    }
                    sx={{ borderRadius: 1 }}
                  >
                    <MenuItem value="">
                      <Typography variant="body2" color="text.secondary">
                        All Stages
                      </Typography>
                    </MenuItem>
                    {stageOptions.map(stage => (
                      <MenuItem key={stage} value={stage}>
                        <Typography variant="body2" fontWeight="medium">
                          {stage.replace('_', ' ').toUpperCase()}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl size="medium" sx={{ width: '100%' }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.active || ''}
                    onChange={(e) => onFilterChange('active', e.target.value === '' ? null : e.target.value)}
                    label="Status"
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              All Status
                            </Typography>
                          </Box>
                        );
                      }
                      return selected === 'true' ? 'Active' : 'Inactive';
                    }}
                    startAdornment={
                      <Box sx={{ mr: 1, color: 'text.secondary' }}>
                        📊
                      </Box>
                    }
                    sx={{ borderRadius: 1 }}
                  >
                    <MenuItem value="">
                      <Typography variant="body2" color="text.secondary">
                        All Status
                      </Typography>
                    </MenuItem>
                    <MenuItem value="true">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                        <Typography variant="body2">Active</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="false">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CancelIcon color="error" fontSize="small" />
                        <Typography variant="body2">Inactive</Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={onClearFilters}
                  startIcon={<FilterIcon />}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 1
                  }}
                >
                  Clear All Filters
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Showing {filteredRules.length} of {rules.length} rules
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
      )}

      {/* Rules Table */}
      <Card sx={{ width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: showCreateButton ? 'space-between' : 'center', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ width: showCreateButton ? 'auto' : '100%', textAlign: 'center' }}>
              📋 {title} ({filteredRules.length})
            </Typography>
            {showCreateButton && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onCreateClick}
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  py: 1
                }}
              >
                Create Rule
              </Button>
            )}
          </Box>

          <TableContainer component={Paper} sx={{ flexGrow: 1, maxHeight: '400px', position: 'relative' }}>
            {filterLoading && (
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
                borderRadius: 1
              }}>
                <Box sx={{ textAlign: 'center' }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Updating results...
                  </Typography>
                </Box>
              </Box>
            )}
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Rule ID</TableCell>
                  <TableCell>Name</TableCell>
                  {projects.length > 0 && <TableCell>Project</TableCell>}
                  <TableCell>Stage</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRules.length > 0 ? (
                  filteredRules.map((rule) => (
                    <TableRow 
                      key={rule.unique_id} 
                      hover
                      onClick={() => onViewClick(rule)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {rule.unique_id}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {rule.name}
                        </Typography>
                      </TableCell>
                      {projects.length > 0 && (
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium" color="primary">
                            {getProjectName(rule.project_id)}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip
                          label={rule.stage.replace('_', ' ').toUpperCase()}
                          color={getStageColor(rule.stage)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={rule.active ? 'Active' : 'Inactive'}
                          color={getStatusColor(rule.active)}
                          size="small"
                          icon={rule.active ? <CheckCircleIcon /> : <CancelIcon />}
                        />
                      </TableCell>
                      <TableCell>
                        {formatDate(rule.create_date)}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="Edit Rule">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditClick(rule);
                              }}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Rule">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteClick(rule);
                              }}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={projects.length > 0 ? 7 : 6} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No rules found matching your criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

    </Box>
  );
}; 